import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { kpiApi, type MyPerformance } from "../api/kpi";
import { formatNumber, intlLocale } from "../lib/format";

/**
 * Compact KPI panel for the Sales / Sales Director dashboard.
 *
 * <p>Shows YTD revenue target vs. an invoiced-order proxy, plus a tiny bar
 * chart of monthly targets. Once the ERP integration ships, the same widget
 * will swap in the real € actuals — the BE already returns
 * {@code actualRevenueEur} (currently null) and {@code attainment}.
 */
export function KpiWidget() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const [data, setData] = useState<MyPerformance | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void kpiApi.me().then(setData).catch((e) => setErr(e?.message ?? String(e)));
  }, []);

  if (err) return <div style={card}>{t("dashboard.kpiWidget.error", "KPI unavailable: {{error}}", { error: err })}</div>;
  if (!data) return <div style={card}>{t("dashboard.kpiWidget.loading", "Loading KPI…")}</div>;

  const targetTotal = Number(data.ytdTargetEur ?? 0);
  const actualTotal = Number(data.ytdActualEur ?? 0);
  const maxTarget = Math.max(1, ...data.months.map((m) => Number(m.targetRevenueEur ?? 0)));

  return (
    <div style={card}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <strong style={{ fontSize: 14, color: "var(--brand-ink)", letterSpacing: 0.2 }}>
          {t("dashboard.kpiWidget.title", "YTD KPI — {{year}}", { year: data.year })}
        </strong>
        <Link to="/admin/kpi" style={{ fontSize: 11, color: "var(--brand-ink)", textDecoration: "none" }}>
          {t("dashboard.kpiWidget.manage", "Manage →")}
        </Link>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
        <Stat label={t("dashboard.kpiWidget.target", "Target")} value={formatEur(targetTotal)} accent="var(--brand-ink)" />
        <Stat
          label={t("dashboard.kpiWidget.actual", "Actual (€)")}
          value={actualTotal > 0 ? formatEur(actualTotal) : "—"}
          hint={actualTotal === 0 ? t("dashboard.kpiWidget.actualHint", "Available after ERP integration") : undefined}
        />
        <Stat label={t("dashboard.kpiWidget.invoicedOrders", "Invoiced orders")} value={String(data.ytdInvoicedOrders)} accent="var(--ok)" />
      </div>

      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 4, alignItems: "end", height: 56 }}>
        {data.months.map((m) => {
          const v = Number(m.targetRevenueEur ?? 0);
          const h = v <= 0 ? 4 : Math.max(4, Math.round((v / maxTarget) * 52));
          const monthDate = new Date(data.year, m.month - 1, 1);
          return (
            <div
              key={m.month}
              title={m.invoicedOrderCount == null
                // A month after the day the invoices reach has no figure yet: a dash, not a zero.
                ? t("dashboard.kpiWidget.monthTooltipNoData", "{{month}} — target {{target}}, invoiced —", {
                  month: monthDate.toLocaleDateString(intlLocale(locale), { month: "short" }),
                  target: v ? "€ " + formatEur(v) : "—",
                })
                : t("dashboard.kpiWidget.monthTooltip", "{{month}} — target {{target}}, {{count}} invoiced", {
                  month: monthDate.toLocaleDateString(intlLocale(locale), { month: "short" }),
                  target: v ? "€ " + formatEur(v) : "—",
                  count: m.invoicedOrderCount,
                })}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
            >
              <div style={{ width: "100%", height: h, background: v ? "var(--brand-primary)" : "var(--surface-muted)", borderRadius: 3 }} />
              <span style={{ fontSize: 9, color: "var(--ink-4)" }}>{monthDate.toLocaleDateString(intlLocale(locale), { month: "narrow" })}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, hint }: { label: string; value: string; accent?: string; hint?: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--ink-4)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: accent ?? "var(--ink)", marginTop: 2 }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: "var(--ink-4)", marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function formatEur(n: number): string {
  return formatNumber(n, undefined, { maximumFractionDigits: 0 });
}

const card: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 10,
  padding: 16,
  boxShadow: "var(--shadow-1)",
};
